import React, { useState, useRef, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import MediaPlayer from '../../../components/media/MediaPlayer';
import { useLessonMediaComplete } from '../../../hooks/useLessonMedia';

const LessonPlayer = ({ 
  lesson, 
  onTimeUpdate, 
  onComplete, 
  currentTime, 
  duration 
}) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [showControls, setShowControls] = useState(true);

  // Usar o novo hook para buscar mídias da lição
  const { 
    lesson: lessonWithMedia, 
    mainVideo, 
    supportingMedia, 
    mainVideoUrl, 
    loading: mediaLoading, 
    error: mediaError 
  } = useLessonMediaComplete(lesson?.id);

  // Debug: log dos dados de mídia
  useEffect(() => {
    console.log('🔍 LessonPlayer Debug:', {
      lessonId: lesson?.id,
      lessonTitle: lesson?.title,
      mainVideo,
      fallbackUrl: lesson?.video_url || lesson?.videoUrl,
      mediaLoading,
      mediaError,
      mainVideoUrl
    });
  }, [lesson?.id, lesson?.title, mainVideo, lesson?.video_url, lesson?.videoUrl, mediaLoading, mediaError, mainVideoUrl]);

  let hideControlsTimeout;

  useEffect(() => {
    const video = videoRef?.current;
    if (!video) return;

    const handleLoadedData = () => setIsLoading(false);
    const handleTimeUpdate = () => {
      if (onTimeUpdate) {
        onTimeUpdate(video?.currentTime, video?.duration);
      }
    };
    const handleEnded = () => {
      setIsPlaying(false);
      if (onComplete) onComplete();
    };

    video?.addEventListener('loadeddata', handleLoadedData);
    video?.addEventListener('timeupdate', handleTimeUpdate);
    video?.addEventListener('ended', handleEnded);

    return () => {
      video?.removeEventListener('loadeddata', handleLoadedData);
      video?.removeEventListener('timeupdate', handleTimeUpdate);
      video?.removeEventListener('ended', handleEnded);
    };
  }, [lesson?.videoUrl, onTimeUpdate, onComplete]);

  useEffect(() => {
    const video = videoRef?.current;
    if (video && currentTime !== video?.currentTime) {
      video.currentTime = currentTime;
    }
  }, [currentTime]);

  const togglePlay = () => {
    const video = videoRef?.current;
    if (!video) return;

    if (isPlaying) {
      video?.pause();
    } else {
      video?.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e) => {
    const video = videoRef?.current;
    if (!video) return;

    const rect = e?.currentTarget?.getBoundingClientRect();
    const clickX = e?.clientX - rect?.left;
    const width = rect?.width;
    const newTime = (clickX / width) * video?.duration;
    
    video.currentTime = newTime;
  };

  const handleVolumeChange = (e) => {
    const video = videoRef?.current;
    const newVolume = parseFloat(e?.target?.value);
    
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
    
    if (video) {
      video.volume = newVolume;
    }
  };

  const toggleMute = () => {
    const video = videoRef?.current;
    if (!video) return;

    const newMuted = !isMuted;
    setIsMuted(newMuted);
    video.volume = newMuted ? 0 : volume;
  };

  const toggleFullscreen = () => {
    const container = videoRef?.current?.parentElement;
    if (!container) return;

    if (!document.fullscreenElement) {
      container?.requestFullscreen?.();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen?.();
      setIsFullscreen(false);
    }
  };

  const changePlaybackRate = () => {
    const rates = [0.5, 0.75, 1, 1.25, 1.5, 2];
    const currentIndex = rates?.indexOf(playbackRate);
    const nextRate = rates?.[(currentIndex + 1) % rates?.length];
    
    setPlaybackRate(nextRate);
    if (videoRef?.current) {
      videoRef.current.playbackRate = nextRate;
    }
  };

  const formatTime = (time) => {
    if (!time || isNaN(time)) return "0:00";
    
    const hours = Math.floor(time / 3600);
    const minutes = Math.floor((time % 3600) / 60);
    const seconds = Math.floor(time % 60);
    
    if (hours > 0) {
      return `${hours}:${minutes?.toString()?.padStart(2, '0')}:${seconds?.toString()?.padStart(2, '0')}`;
    }
    return `${minutes}:${seconds?.toString()?.padStart(2, '0')}`;
  };

  const handleMouseMove = () => {
    setShowControls(true);
    clearTimeout(hideControlsTimeout);
    hideControlsTimeout = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (lesson?.lessonType === 'text') {
    return (
      <div className="bg-card rounded-lg p-8 shadow-tier-1">
        <div className="prose prose-lg max-w-none">
          <div className="mb-6 p-4 bg-secondary/10 rounded-lg">
            <div className="flex items-center space-x-2 text-secondary">
              <Icon name="FileText" size={20} />
              <span className="font-medium">Lição em Texto</span>
            </div>
          </div>
          
          <div dangerouslySetInnerHTML={{ __html: lesson?.content || lesson?.description }} />
          
          <div className="mt-8 pt-6 border-t border-border">
            <Button onClick={onComplete} className="w-full">
              Marcar como Concluída
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div 
      className="relative bg-card rounded-lg overflow-hidden shadow-tier-2"
      onMouseMove={handleMouseMove}
    >
      {/* Video Container */}
      <div className="relative aspect-video bg-black">
        {mediaLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <div className="flex flex-col items-center space-y-4 text-white">
              <div className="animate-spin">
                <Icon name="Loader2" size={40} />
              </div>
              <p>Carregando mídia da lição...</p>
            </div>
          </div>
        )}

        {mediaError && (
          <div className="absolute inset-0 flex items-center justify-center bg-black">
            <div className="text-white text-center">
              <Icon name="AlertCircle" size={48} className="mx-auto mb-4" />
              <p>Erro ao carregar mídia</p>
              <p className="text-sm text-gray-400 mt-2">Usando vídeo padrão</p>
            </div>
          </div>
        )}

        {/* Novo MediaPlayer com suporte a múltiplos providers */}
        <MediaPlayer
          mediaMapping={mainVideo}
          fallbackUrl={lesson?.video_url || lesson?.videoUrl}
          onTimeUpdate={onTimeUpdate}
          onComplete={onComplete}
          currentTime={currentTime}
          duration={duration}
          className="w-full h-full"
        />
      </div>

      {/* Lesson Info Overlay */}
      <div className="p-6 bg-card">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h1 className="text-2xl font-heading font-semibold text-foreground mb-2">
              {lesson?.title}
            </h1>
            <div className="flex items-center space-x-4 text-sm text-muted-foreground">
              <span>Módulo {lesson?.moduleNumber}</span>
              <span>•</span>
              <span>Lição {lesson?.lessonNumber}</span>
              <span>•</span>
              <span>{lesson?.estimatedTime}</span>
            </div>
          </div>
          
          <div className="flex items-center space-x-2 ml-4">
            <div className="text-sm text-muted-foreground">
              {Math.round(progress)}% concluído
            </div>
            <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary rounded-full transition-state"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        </div>

        {/* Mídias de Apoio */}
        {supportingMedia && supportingMedia.length > 0 && (
          <div className="mt-6 pt-6 border-t border-border">
            <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center">
              <Icon name="Paperclip" size={20} className="mr-2" />
              Materiais de Apoio
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {supportingMedia.map((media, index) => (
                <div key={media.id} className="bg-secondary/10 rounded-lg p-4 hover:bg-secondary/20 transition-colors">
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      {media.media_type === 'video' && <Icon name="Play" size={20} className="text-primary" />}
                      {media.media_type === 'document' && <Icon name="FileText" size={20} className="text-primary" />}
                      {media.media_type === 'image' && <Icon name="Image" size={20} className="text-primary" />}
                      {media.media_type === 'link' && <Icon name="ExternalLink" size={20} className="text-primary" />}
                      {media.media_type === 'audio' && <Icon name="Volume2" size={20} className="text-primary" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-sm font-medium text-foreground truncate">
                        {media.title}
                      </h4>
                      <p className="text-xs text-muted-foreground capitalize">
                        {media.media_type} • {media.provider}
                      </p>
                      <a 
                        href={media.library_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-xs text-primary hover:underline mt-1 inline-block"
                      >
                        Acessar material
                      </a>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default LessonPlayer;
